import { Duration, LocalDate, LocalDateTime, ZoneId } from "@js-joda/core";
import React, { CSSProperties } from "react";
import {
  ClockedInStatus,
  PatientTaskInstance,
  PatientTaskInstanceId,
  VisitInstanceDetails,
} from "../messages";
import { STRINGS } from "../strings";
import { globalize, translate } from "../translate";
import { doNothing } from "../utils/doNothing";
import { CLOCK_SVG } from "../utils/svgs";
import ClockedInButton from "./ClockedInButton";
import ClockedOutButton from "./ClockedOutButton";
import ClockInOutButtons from "./ClockInOutButtons";
import { AppButton } from "./Generic/AppButton";
import { AppCard, AppCardContent } from "./Generic/AppCard";
import { AppIcon } from "./Generic/AppIcon";
import { AppListItem } from "./Generic/AppList";
import Flex from "./Generic/Flex";
import { If } from "./Generic/If";
import { InfoCard } from "./Generic/InfoCard";
import TaskAvatar from "./TaskAvatar";

type Props<$PatientTaskInstance extends PatientTaskInstance> = {
  task: $PatientTaskInstance;
  clockedInStatus: ClockedInStatus;
  style?: CSSProperties | undefined;
  onClick: (patientTaskInstanceId: PatientTaskInstanceId, forceOpenScheduleDate: boolean) => void;
} & ($PatientTaskInstance extends PatientTaskInstance.SCHEDULED | PatientTaskInstance.COMPLETED
  ? {
      onClickClockIn: (visitDetails: VisitInstanceDetails) => void;
      onClickClockOut: (visitDetails: VisitInstanceDetails) => void;
      onClickStartNow: (visitDetails: VisitInstanceDetails) => void;
    }
  : {});

const TaskCard = <$PatientTaskInstance extends PatientTaskInstance>(
  props: Props<$PatientTaskInstance>
) => {
  const handleActionClick = React.useCallback(() => {
    props.onClick(props.task.id, false);
  }, [props]);

  const fromToAmpmText = React.useMemo(() => {
    if (props.task.scheduleDateTime === null || props.task.taskDetails.durationMinutes === null) {
      return "";
    }

    const zoneId = ZoneId.of("America/New_York");
    const startTime = LocalDateTime.ofInstant(props.task.scheduleDateTime, zoneId);
    const endTime = startTime.plus(Duration.ofMinutes(props.task.taskDetails.durationMinutes));

    const fromAmpmText = globalize().formatLocalDateTime(startTime, { time: "short" });
    const toAmpmText = globalize().formatLocalDateTime(endTime, { time: "short" });

    return `${fromAmpmText} - ${toAmpmText}`;
  }, [props.task.scheduleDateTime, props.task.taskDetails.durationMinutes]);

  const currentYear = LocalDate.now().year();

  const taskDatesRange = React.useMemo(() => {
    const startDate = globalize()
      .formatLocalDate(props.task.startDate, { date: "medium" })
      .split(",")[0];
    const dueDate = globalize()
      .formatLocalDate(props.task.dueDate, { date: "medium" })
      .split(",")[0];

    const taskDueDateYear = props.task.dueDate.year();
    const yearsDiff = taskDueDateYear - currentYear;

    let dueDateYear;
    switch (yearsDiff) {
      case 0:
        dueDateYear = null;
        break;
      case 1:
        dueDateYear = `(${translate(STRINGS.NEXT_YEAR)})`;
        break;
      default:
        dueDateYear = `${taskDueDateYear}`;
    }

    let taskDatesRangeText = translate(STRINGS.DATES_RANGE_TO_COMPLETE_TASKS_FROM_TO)
      .replace("%start%", startDate)
      .replace("%end%", dueDate);

    if (dueDateYear !== null) {
      taskDatesRangeText += ` ${dueDateYear}`;
    }

    return taskDatesRangeText;
  }, [props.task.startDate, props.task.dueDate, currentYear]);

  const renderedTaskAction = React.useMemo(() => {
    if (props.task.scheduleDateTime === null) {
      return (
        <AppButton
          variant="filled"
          color="primary"
          size="small"
          textColor="white"
          className="TaskCard__action-button"
          onClick={handleActionClick}
        >
          {translate(STRINGS.SCHEDULE)}
        </AppButton>
      );
    }

    return (
      <InfoCard
        title={translate(STRINGS.SCHEDULED_FOR)}
        color={props.task.patientConfirmedSchedule ? "primary" : "warning"}
        variant="soft"
        className="TaskCard_schedule-hours"
      >
        <AppListItem
          leading={<AppIcon icon={CLOCK_SVG} stroke="var(--primary)" />}
          title={fromToAmpmText}
        />
        <br />
        {props.task.patientConfirmedSchedule
          ? "Patient confirmed schedule"
          : "Pending patient confirmation"}
      </InfoCard>
    );
  }, [props, fromToAmpmText, handleActionClick]);

  const canClockInAndOut = (
    $props: Props<PatientTaskInstance>
  ): $props is Props<PatientTaskInstance.SCHEDULED | PatientTaskInstance.COMPLETED> => {
    return (
      ($props.task.type === "SCHEDULED" || $props.task.type === "COMPLETED") &&
      $props.task.visitInstance !== null
    );
  };

  const renderedClockInOutButtons =
    props.task.type === "COMPLETED" &&
    props.task.clockInTime !== null &&
    props.task.clockOutTime !== null ? (
      <AppCardContent className="AssignedItemCard__ClockButtons">
        <ClockedInButton
          clockInDateTime={props.task.clockInTime}
          manualClockTimeEditApproved={false}
          onClick={doNothing}
        />
        <ClockedOutButton
          clockOutDateTime={props.task.clockOutTime}
          manualClockTimeEditApproved={false}
          onClick={doNothing}
        />
      </AppCardContent>
    ) : canClockInAndOut(props) && props.task.visitInstance !== null ? (
      <AppCardContent className="AssignedItemCard__ClockButtons">
        <ClockInOutButtons
          visitDetails={props.task.visitInstance}
          clockedInStatus={props.clockedInStatus}
          onClickClockIn={props.onClickClockIn}
          onClickClockOut={props.onClickClockOut}
          onClickStartNow={props.onClickStartNow}
        />
      </AppCardContent>
    ) : null;

  return (
    <AppCard
      key={props.task.id.toString()}
      className="TaskCard"
      onClick={() => props.onClick(props.task.id, false)}
      style={props.style}
    >
      <AppCardContent>
        <Flex justifyContent="space-between">
          <div className="TaskCard__content">
            <TaskAvatar
              className="TaskCard__avatar"
              variant={props.task.scheduleDateTime === null ? "filled" : "soft"}
              isCompleted={props.task.type === "COMPLETED"}
            />
            <div className="TaskCard__details">
              <span className="TaskCard__patient-name">{props.task.taskDetails.patientName}</span>
              <span className="TaskCard__title">{props.task.taskDetails.title}</span>
              <div className="TaskCard__date-range">
                <div>
                  <span>{taskDatesRange}</span>
                </div>
              </div>
            </div>
          </div>
          <If condition={props.task.type === "PENDING"}>
            <span className="TaskCard__label TaskCard__label__new-task">
              {props.task.dueDate.isBefore(LocalDate.now())
                ? translate(STRINGS.OVERDUE)
                : translate(STRINGS.NEW)}
            </span>
          </If>
          <If condition={props.task.type === "COMPLETED"}>
            <span className="TaskCard__label TaskCard__label__completed-task">
              {translate(STRINGS.DONE)}
            </span>
          </If>
        </Flex>
        {renderedTaskAction}
      </AppCardContent>
      {renderedClockInOutButtons}
    </AppCard>
  );
};

export default TaskCard;
